import { useEffect, useState } from 'react';
import { IDrawerModalAnimationOrigin } from '../../components/CareMapDrawerPage';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../../caremap/CareMapState';
import { useSubscription } from '@meraki-internal/state';
import { TextField } from '../../components/TextField';
import { MemberAvatar } from '../MemberAvatar';
import { DateField } from '../../components/DateField';
import { DrawerSectionHeading } from '../../components/DrawerSectionHeading';
import { EditableList } from '../../components/EditableList';
import { DiagnosisSelectField } from '../DiagnosisSelectField';
import { AllergySelectField } from '../AllergySelectField';
import { IAllergy, IContactMethod, IDiagnosis } from '../../caremap/ICareMap';
import { LabelProvider } from '../../config/LabelProvider';
import { EditPatientAvatarDrawer } from '../../avatar/EditAvatarDrawer';
import { PatientFormState } from '../../avatar/PatientFormState';
import { PrivateCheckbox } from '../../components/PrivateCheckbox';
import { EmailField, IEmailContactMethod } from '../../contact-methods/EmailField';
import { IPhoneContactMethod, PhoneField } from '../../contact-methods/PhoneField';
import { LinkedMemberEmailField } from '../../caremap/LinkedMemberEmailField';

export const ActivePatientEditForm: React.FC = () => {
    const labelProvider = useInstance(LabelProvider);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const [showingAvatarPicker, setIsShowingAvatarPicker] = useState(false);
    const [avatarDrawerAnimationOrigin, setAvatarDrawerAnimationOrigin] = useState<IDrawerModalAnimationOrigin>();
    const avatarClicked = (e: React.MouseEvent) => {
        setAvatarDrawerAnimationOrigin({x: e.clientX, y: e.clientY});
        setIsShowingAvatarPicker(true);
    };

    const patientForm = useInstance(PatientFormState);
    useSubscription(() => patientForm);

    const patient = patientForm.state.patient;

    useEffect(() => {
        const caremap = careMapState.getActiveCaremap();
        if (caremap){
            patientForm.init(caremap.patient);
        }
        return () => {
            patientForm.clear();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Boolean(careMapState.state), patientForm]);

    if (!careMapState.state || !patient) {
        return null;
    }

    const contactMethodChanged = (method: IContactMethod) => {
        const copy = [ ...patient.contactMethods ];
        const idx = copy.findIndex(m => m.type === method.type);

        if (idx > -1) {
            copy.splice(idx, 1, method);
        } else {
            copy.push(method);
        }

        patientForm.patch({ contactMethods: copy });
    };

    // Unlink: clear the email contact method and persist via the normal save path so the server
    // drops linkedUserId. Also clear linkedUserId optimistically so the form immediately flips
    // back to the editable EmailField rather than waiting for the post-save refresh.
    const onUnlink = () => {
        const contactMethods = patient.contactMethods.filter(m => m.type !== 'email');
        patientForm.patch({ contactMethods, linkedUserId: undefined });
        careMapState.updatePatient({ ...patient, contactMethods, linkedUserId: undefined });
    };

    return (
        <>
            <div style={{display: 'flex', flexDirection: 'column', gap: 20}}>

                <div style={{display: 'flex', alignItems: 'start', width: '100%', gap: 20}}>
                    <MemberAvatar
                        // this doesn't use the "form" patient b/c it can't be changed by the form fields
                        // and b/c it can be changed in CareMapState by EditAvatarDrawer
                        member={patient}
                        onClick={(e: React.MouseEvent) => avatarClicked(e)}
                    />

                    <div style={{display: 'flex', flexDirection: 'column', gap: 20, flexGrow: 1}}>
                        <TextField
                            name="first-name"
                            label="First Name"
                            value={patient.firstName}
                            autocapitalize="words"
                            onInput={firstName => patientForm.patch({ firstName })}
                        />
                        <TextField
                            name="last-name"
                            label="Last Name"
                            value={patient.lastName}
                            autocapitalize="words"
                            onInput={lastName => patientForm.patch({ lastName })}
                        />
                    </div>
                </div>

                <DateField label="Date of Birth" data-type="dob" value={patient.dob} onChange={dob => patientForm.patch({ dob })} />

                <TextField
                    name="address"
                    label="Address"
                    value={patient.address}
                    autocapitalize="words"
                    onInput={address => patientForm.patch({ address })}
                />

                {patient.linkedUserId ? (
                    <LinkedMemberEmailField
                        email={careMapState.getContactMethodOrDefault(patient, 'email').value}
                        linkedToName={patient.firstName || 'this person'}
                        onUnlink={onUnlink}
                        isSelf={careMapState.isMyMember(patient)}
                    />
                ) : (
                    <EmailField
                        methodChanged={contactMethodChanged}
                        method={careMapState.getContactMethodOrDefault(patient, 'email') as IEmailContactMethod}
                    />
                )}
                <PhoneField
                    methodChanged={contactMethodChanged}
                    method={careMapState.getContactMethodOrDefault(patient, 'mobile-phone') as IPhoneContactMethod}
                />
                <PhoneField
                    methodChanged={contactMethodChanged}
                    method={careMapState.getContactMethodOrDefault(patient, 'work-phone') as IPhoneContactMethod}
                />

                <div>
                    <DrawerSectionHeading text="Diagnoses">
                        <PrivateCheckbox
                            data-id="diagnoses-visibility-checkbox"
                            checked={!patient.diagnosesIsPublic!}
                            onChange={isPrivate => patientForm.patch({diagnosesIsPublic: !isPrivate})}
                        />
                    </DrawerSectionHeading>
                    <div data-type="diagnoses" style={{display: 'flex', flexDirection: 'column'}}>
                        <EditableList
                            items={patient.diagnoses || []}
                            isBlank={m => !m.diagnosis}
                            buttonLabel="+ Add Diagnosis"
                            buttonDataType="diagnoses-add-item"
                            buttonFill="outline"
                            createNew={(): IDiagnosis => ({})}
                            onChange={diagnoses => patientForm.patch({ diagnoses })}

                            renderRow={({ item, focusRef, onChange, onBlur }) => (
                                <DiagnosisSelectField
                                    innerRef={focusRef}
                                    diagnosisId={item.diagnosisId}
                                    diagnosis={item.diagnosis}
                                    onChange={d => onChange({...item, ...d})}
                                    onBlur={onBlur}
                                    label={labelProvider.getLabel('CARE_RECIPIENT_DIAGNOSIS')}
                                    marginBottom={20}
                                />
                            )}
                        />
                    </div>
                </div>

                <div>
                    <DrawerSectionHeading text="Allergies">
                        <PrivateCheckbox
                            data-id="allergies-visibility-checkbox"
                            checked={!patient.allergiesIsPublic!}
                            onChange={isPrivate => patientForm.patch({allergiesIsPublic: !isPrivate})}
                        />
                    </DrawerSectionHeading>
                    <div data-type="allergies" style={{display: 'flex', flexDirection: 'column'}}>
                        <EditableList
                            items={patient.allergies || []}
                            isBlank={m => !m.allergy}
                            buttonLabel="+ Add Allergen"
                            buttonDataType="allergies-add-item"
                            buttonFill="outline"
                            createNew={(): IAllergy => ({})}
                            onChange={allergies => patientForm.patch({ allergies })}

                            renderRow={({ item, focusRef, onChange, onBlur }) => (
                                <AllergySelectField
                                    innerRef={focusRef}
                                    allergyId={item.allergyId}
                                    allergy={item.allergy}
                                    onChange={d => onChange({...item, ...d})}
                                    onBlur={onBlur}
                                    label={labelProvider.getLabel('CARE_RECIPIENT_ALLERGY')}
                                    marginBottom={20}
                                />
                            )}
                        />
                    </div>
                </div>

            </div>
            <EditPatientAvatarDrawer
                isOpen={showingAvatarPicker}
                animationOrigin={avatarDrawerAnimationOrigin}
                patient={patient}
                onSave={changed => {
                    patientForm.patch(changed);
                    setIsShowingAvatarPicker(false);
                }}
                onCancel={() => setIsShowingAvatarPicker(false)}
            />
        </>
    );
};
